import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CalendarDays, Clock, MapPin, UserCheck, ArrowLeft, ArrowRight,
  Loader2, CheckCircle2, Copy, ExternalLink,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Light theme matching other Cohort Detail modals.
const COLORS = {
  bg: "#ffffff",
  border: "#e2e6ef",
  borderMuted: "#eef1f5",
  title: "#062546",
  body: "#5a6577",
  subtle: "#8896a7",
  surface: "#f7f9fc",
  hover: "#eef3fb",
  primary: "var(--primary-color, #4A90D9)",
};

export default function EventWizard({ open, onClose, cohortId, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", date: "", start_time: "", end_time: "", location: "", instructor: "" });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const reset = () => {
    setStep(1);
    setForm({ name: "", date: "", start_time: "", end_time: "", location: "", instructor: "" });
    setSubmitting(false);
    setCreated(null);
  };
  const handleClose = () => { reset(); if (onClose) onClose(); };

  const canNext = () => {
    if (step === 1) return form.name.trim().length > 0;
    if (step === 2) return form.date && form.start_time;
    return true;
  };

  const checkinUrl = created ? `${window.location.origin}/checkin/${created.id}` : "";

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/cohorts/${cohortId}/events`,
        { ...form, name: form.name.trim() },
        { withCredentials: true }
      );
      setCreated(data);
      setStep(6);
      if (onCreated) onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create event");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-lg max-h-[90vh] p-0 flex flex-col overflow-hidden"
        style={{ backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 16 }}
        data-testid="event-wizard"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0" style={{ borderBottom: `1px solid ${COLORS.borderMuted}` }}>
          <DialogTitle className="font-heading font-bold text-xl flex items-center gap-2" style={{ color: COLORS.title }}>
            <CalendarDays className="h-4 w-4" style={{ color: COLORS.primary }} />
            Event Wizard
          </DialogTitle>
          <DialogDescription className="font-body text-xs" style={{ color: COLORS.subtle }}>
            {step < 6 ? `Step ${step} of 5 — create a new session for this cohort` : "Event created"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {step === 1 && (
            <div className="space-y-3" data-testid="event-step-1">
              <h3 className="text-lg font-heading font-bold" style={{ color: COLORS.title }}>Name this session</h3>
              <p className="text-sm font-body" style={{ color: COLORS.subtle }}>
                e.g. "Day 1," "Morning Session," "Kickoff — April 22"
              </p>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Session name"
                data-testid="event-name-input"
                className="h-10 text-sm font-body"
                style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
                autoFocus
              />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="event-step-2">
              <h3 className="text-lg font-heading font-bold flex items-center gap-2" style={{ color: COLORS.title }}>
                <Clock className="h-4 w-4" style={{ color: COLORS.primary }} /> Date &amp; time
              </h3>
              <div className="space-y-1.5">
                <Label className="font-body text-xs uppercase tracking-wider" style={{ color: COLORS.subtle }}>Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  data-testid="event-date-input"
                  className="h-10 text-sm font-body"
                  style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="font-body text-xs uppercase tracking-wider" style={{ color: COLORS.subtle }}>Start</Label>
                  <Input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                    data-testid="event-start-input"
                    className="h-10 text-sm font-body"
                    style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-body text-xs uppercase tracking-wider" style={{ color: COLORS.subtle }}>End (optional)</Label>
                  <Input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                    data-testid="event-end-input"
                    className="h-10 text-sm font-body"
                    style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="event-step-3">
              <h3 className="text-lg font-heading font-bold flex items-center gap-2" style={{ color: COLORS.title }}>
                <MapPin className="h-4 w-4" style={{ color: COLORS.primary }} /> Location
              </h3>
              <p className="text-sm font-body" style={{ color: COLORS.subtle }}>
                Building and room, or free text.
              </p>
              <Input
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                placeholder="e.g. Simulation Center — Room 101"
                data-testid="event-location-input"
                className="h-10 text-sm font-body"
                style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
              />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="event-step-4">
              <h3 className="text-lg font-heading font-bold flex items-center gap-2" style={{ color: COLORS.title }}>
                <UserCheck className="h-4 w-4" style={{ color: COLORS.primary }} /> Instructor
              </h3>
              <p className="text-sm font-body" style={{ color: COLORS.subtle }}>
                Who's leading this session?
              </p>
              <Input
                value={form.instructor}
                onChange={(e) => setForm((f) => ({ ...f, instructor: e.target.value }))}
                placeholder="Instructor name"
                data-testid="event-instructor-input"
                className="h-10 text-sm font-body"
                style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
              />
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="event-step-5">
              <h3 className="text-lg font-heading font-bold" style={{ color: COLORS.title }}>Review &amp; confirm</h3>
              <div className="rounded-lg p-4 space-y-2" style={{ border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface }}>
                <Row label="Name" value={form.name} />
                <Row label="Date" value={form.date} />
                <Row label="Time" value={`${form.start_time}${form.end_time ? ` – ${form.end_time}` : ""}`} />
                <Row label="Location" value={form.location || "—"} />
                <Row label="Instructor" value={form.instructor || "—"} />
              </div>
              <p className="text-xs font-body" style={{ color: COLORS.subtle }}>
                A shareable check-in URL will be generated for this session.
              </p>
            </div>
          )}

          {step === 6 && created && (
            <div className="space-y-4" data-testid="event-step-success">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" style={{ color: "#047857" }} />
                <h3 className="text-lg font-heading font-bold" style={{ color: COLORS.title }}>Event created</h3>
              </div>
              <p className="text-sm font-body" style={{ color: COLORS.body }}>
                Share this URL at the door — learners check in without logging in.
              </p>
              <div className="rounded-lg p-3 flex items-center gap-2" style={{ border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface }}>
                <code className="flex-1 text-xs font-mono truncate" style={{ color: COLORS.title }} data-testid="event-checkin-url">
                  {checkinUrl}
                </code>
                <button
                  onClick={() => { navigator.clipboard.writeText(checkinUrl); toast.success("Link copied"); }}
                  className="shrink-0 p-2 rounded hover:bg-white transition-colors"
                  title="Copy link"
                  data-testid="event-copy-link-btn"
                >
                  <Copy className="h-3.5 w-3.5" style={{ color: COLORS.body }} />
                </button>
                <button
                  onClick={() => window.open(checkinUrl, "_blank")}
                  className="shrink-0 p-2 rounded hover:bg-white transition-colors"
                  title="Open in new tab"
                  data-testid="event-open-link-btn"
                >
                  <ExternalLink className="h-3.5 w-3.5" style={{ color: COLORS.body }} />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center px-6 py-3 shrink-0" style={{ borderTop: `1px solid ${COLORS.borderMuted}`, backgroundColor: COLORS.bg }}>
          <Button variant="ghost" onClick={handleClose} className="font-body" style={{ color: COLORS.body }} data-testid="event-wizard-cancel">
            {step === 6 ? "Done" : "Cancel"}
          </Button>
          <div className="flex gap-2">
            {step > 1 && step < 6 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="h-9 font-body"
                style={{ borderColor: COLORS.border, color: COLORS.body, backgroundColor: "#fff" }}
                data-testid="event-wizard-back"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
              </Button>
            )}
            {step < 5 && (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canNext()}
                className="h-9 text-white font-body"
                style={{ backgroundColor: COLORS.primary }}
                data-testid="event-wizard-next"
              >
                Next <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
            {step === 5 && (
              <Button
                onClick={handleCreate}
                disabled={submitting}
                className="h-9 text-white font-body"
                style={{ backgroundColor: COLORS.primary }}
                data-testid="event-wizard-create"
              >
                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                Create event
              </Button>
            )}
            {step === 6 && (
              <Button
                onClick={() => navigate(`/checkin/${created.id}`)}
                className="h-9 text-white font-body"
                style={{ backgroundColor: COLORS.primary }}
                data-testid="event-wizard-preview"
              >
                Preview check-in <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm font-body">
      <span className="text-[11px] uppercase tracking-wider shrink-0 pt-0.5" style={{ color: COLORS.subtle, minWidth: 78 }}>{label}</span>
      <span className="flex-1" style={{ color: COLORS.title }}>{value}</span>
    </div>
  );
}
